import { useMemo } from 'react';
import { useLanguage } from './useLanguage';
import { useTranslations } from '../../translation/TranslationContext';
import { useMatchTime } from './useMatchTime';

export interface MatchTimeData {
    minutes_played: number;
    seconds_played: number;
    is_running: boolean;
    home_score?: number;
    away_score?: number;
    isEnded?: boolean;
    isFullTime?: boolean;
    isHalfTime?: boolean;
    isExtraTimeFirstHalf?: boolean;
    isExtraTimeSecondHalf?: boolean;
    isHalfTimeOfExtraTime?: boolean;
    isPenalties?: boolean;
    isPreMatch?: boolean;
    isEndOfSecondHalf?: boolean;
    isEndOfExtraTime?: boolean;
    minute?: number;
    time_start?: string;
    currentPeriod?: number;
}

interface UseMatchTimeDisplayProps {
    isTimerOn: boolean;
    matchClockData: MatchTimeData | null;
    isEnabled?: boolean;
    isEnded?: boolean;
    isHalfTimeFirstEvent?: boolean;
    isFullTimeFirstEvent?: boolean;
    isEventAfterFullTime?: boolean;
    isExtraTimeFirstEvent?: boolean;
    isPenaltiesFirstEvent?: boolean;
    isEventAfterPenalties?: boolean;
    penaltyResult?: string | null;
    penaltiesAreStarted?: boolean;
    shouldShowPenaltyAfterMatchEnd?: boolean;
}

export const useMatchTimeDisplay = ({
    matchClockData,
    isEnabled = true,
    isEnded,
    isHalfTimeFirstEvent,
    isFullTimeFirstEvent,
    isEventAfterFullTime,
    penaltyResult,
    penaltiesAreStarted,
    shouldShowPenaltyAfterMatchEnd,
    isTimerOn = false
}: UseMatchTimeDisplayProps) => {
    const language = useLanguage();
    const t = useTranslations(language);
    
    const { minutes, seconds, isRunning, formattedTime } = useMatchTime({
        matchClockData: matchClockData || null,
        isEnabled
    });

    const displayText = useMemo(() => {
        if (isEnded && shouldShowPenaltyAfterMatchEnd && penaltyResult) {
            return `${t['InHouseStats.OverviewScoreboardPen']} ${penaltyResult}`;
        }

        if (isEnded || matchClockData?.isEnded || matchClockData?.isFullTime) {
            return t['InHouseStats.OverviewScoreboardFinish'];
        }
        
        if (penaltyResult) {
            return `${t['InHouseStats.OverviewScoreboardPen']} ${penaltyResult}`;
        }
        
        if (penaltiesAreStarted || matchClockData?.isPenalties) {
            return `${t['InHouseStats.OverviewScoreboardPen']} 0-0`;
        }
        
        if ((isHalfTimeFirstEvent && !isRunning) || matchClockData?.isHalfTime) {
            return t['InHouseStats.OverviewHalfTime'];
        }
        
        return isTimerOn ? formattedTime || '0:00' : '\u00A0'; 
    }, [
        isEnded,
        matchClockData?.isEnded,
        isFullTimeFirstEvent,
        isEventAfterFullTime,
        penaltyResult,
        penaltiesAreStarted,
        shouldShowPenaltyAfterMatchEnd,
        matchClockData?.isPenalties,
        isHalfTimeFirstEvent,
        isRunning,
        matchClockData?.isHalfTime,
        formattedTime,
        t['InHouseStats.OverviewScoreboardFinish'],
        t['InHouseStats.OverviewScoreboardPen'],
        t['InHouseStats.OverviewHalfTime'],
        isTimerOn
    ]);

    return {
        minutes,
        seconds,
        isRunning,
        formattedTime,
        displayText,
        matchClockData
    };
};
